import { useEffect, useState } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Phone, MessageCircle, CalendarCheck } from "lucide-react";
import { Link } from "react-router-dom";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", body: "" });
  const [sending, setSending] = useState(false);
  const [settings, setSettings] = useState({});
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault(); setSending(true);
    try { await api.post("/messages", form); toast.success("Message sent — we'll get back to you shortly.");
          setForm({ name: "", email: "", phone: "", subject: "", body: "" });
    } catch { toast.error("Could not send message."); } finally { setSending(false); }
  };

  const phone = settings.phone || "+234 813 977 1270";
  const wa = (settings.whatsapp || phone).replace(/\D/g, "");

  return (
    <div className="min-h-screen ankara" data-testid="contact-page">
      <Header />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 md:pt-20 pb-12">
        <div className="chip chip-emerald mb-3">Contact</div>
        <h1 className="font-display text-4xl md:text-5xl leading-[1.05]">Contact Jay's Veterinary Consult and Services.</h1>
        <p className="mt-4 text-[#57534E] max-w-2xl">Reach Dr. Juwon by phone, WhatsApp, or by booking a consultation online.</p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="contact-buttons">
          <a href={`tel:+${wa}`} className="p-6 rounded-2xl bg-white border border-[#EAD9B0] card-lift" data-testid="btn-call">
            <div className="h-11 w-11 rounded-xl bg-[#0F766E]/10 flex items-center justify-center"><Phone size={20} className="text-[#0F766E]" /></div>
            <div className="font-display text-xl mt-4">Call Us</div>
            <p className="text-sm text-[#57534E] mt-1">{phone}</p>
            <div className="mt-4 btn-primary text-center py-2.5 rounded-full text-xs font-semibold">Tap to call</div>
          </a>
          <a href={`https://wa.me/${wa}?text=${encodeURIComponent("Hello Dr. Juwon, I'd like to consult about my pet.")}`} target="_blank" rel="noopener noreferrer" className="p-6 rounded-2xl bg-white border border-[#EAD9B0] card-lift" data-testid="btn-whatsapp">
            <div className="h-11 w-11 rounded-xl bg-[#25D366]/15 flex items-center justify-center"><MessageCircle size={20} className="text-[#128C7E]" /></div>
            <div className="font-display text-xl mt-4">Chat on WhatsApp</div>
            <p className="text-sm text-[#57534E] mt-1">Message Dr. Juwon directly</p>
            <div className="mt-4 btn-whatsapp text-center py-2.5 rounded-full text-xs font-semibold">Open WhatsApp</div>
          </a>
          <Link to="/consult" className="p-6 rounded-2xl bg-white border border-[#EAD9B0] card-lift" data-testid="btn-consult">
            <div className="h-11 w-11 rounded-xl bg-[#C2410C]/10 flex items-center justify-center"><CalendarCheck size={20} className="text-[#C2410C]" /></div>
            <div className="font-display text-xl mt-4">Book a Consultation</div>
            <p className="text-sm text-[#57534E] mt-1">Submit a consultation form</p>
            <div className="mt-4 btn-terra text-center py-2.5 rounded-full text-xs font-semibold">Start form</div>
          </Link>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <form onSubmit={submit} className="bg-white border border-[#EAD9B0] rounded-3xl p-8" data-testid="contact-form">
          <h2 className="font-display text-2xl mb-4">Send a message</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="contact-name" required />
            <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="contact-email" required />
            <Input label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="contact-phone" />
            <Input label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testId="contact-subject" required />
          </div>
          <div className="mt-4">
            <label className="text-xs uppercase tracking-widest text-[#57534E]">Message</label>
            <textarea rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required data-testid="contact-body"
              className="mt-2 w-full rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] p-3 focus:outline-none focus:ring-2 focus:ring-[#0F766E]" />
          </div>
          <button disabled={sending} className="mt-6 btn-primary px-8 py-3 rounded-full text-sm font-semibold disabled:opacity-60" data-testid="contact-submit">
            {sending ? "Sending…" : "Send message"}
          </button>
        </form>
      </section>
      <Footer />
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#57534E]">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testId}
        className="mt-2 w-full rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] p-3 focus:outline-none focus:ring-2 focus:ring-[#0F766E]" />
    </div>
  );
}
